import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthCtx } from '../context/AuthContext';
import { redirectByRole } from '../lib/redirectByRole';
import MainLandingPage from '../components/MainLandingPage';
import { readAuthHint } from '../lib/authHint';

export default function RootRoute() {
  const { user, profile, loading, profileReady } = useAuthCtx();
  const navigate = useNavigate();
  const location = useLocation();
  // Read once per mount: while auth is still resolving, a returning signed-in visitor gets the
  // neutral state instead of the landing page (the prerender gate already kept the static copy
  // off screen for them, so there is no prerendered markup to hydrate against in that case).
  const [probablySignedIn] = useState(readAuthHint);

  useEffect(() => {
    if (loading) return;
    if (!user) return;
    // Wait for the first profile snapshot. After it, a missing profile (or one without a role) is
    // real: this person signed in but never picked EMT / employer. redirectByRole sends them to the
    // role choice. They used to get the marketing page with "Sign in" / "Create account": a dead end.
    if (!profileReady) return;

    redirectByRole(profile ?? null, navigate, { currentPath: location.pathname });
  }, [loading, user, profile, profileReady, navigate, location.pathname]);

  // Signed-out visitors, and the initial auth-resolving paint, get the real landing page: it must
  // match the prerendered markup React hydrates against, and OAuth reviewers / crawlers must see
  // real purpose copy rather than a spinner.
  //
  // A signed-in user never sees it, with or without a profile. Once auth has resolved to a user,
  // hold a neutral state until the first profile snapshot arrives and redirectByRole can send them
  // on: rendering the landing page in that window is exactly the "landing flash" between screens.
  if ((loading && probablySignedIn) || (user && !loading)) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3 bg-[var(--surface-page)]">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-[var(--border-strong)] border-t-[var(--rr-red)]" aria-hidden />
        <p className="text-[var(--text-secondary)] text-sm font-medium">Redirecting…</p>
      </div>
    );
  }

  return <MainLandingPage />;
}
